/**
 * Commands are requests to change the ledger ("add this item"). Every channel (website,
 * Discord, AI, the Sheet import) builds one of these and sends it through the same pipeline.
 *
 * Each command has a Zod schema, so bad input from any channel is rejected with a readable
 * message before it reaches the rules in decide.ts.
 *
 * Ids (itemId, personId, paymentId) are chosen by the client. That lets the phone show a new
 * item instantly while offline, and lets a retried request be recognized as the same item.
 */
import { z } from "zod";

const id = z.string().min(1).max(80);
const name = z.string().trim().min(1, "Name can't be empty").max(80);
const cents = z.number().int("Amounts are whole cents").min(-10_000_000).max(10_000_000);
const bp = z.number().int().min(0).max(10_000);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Dates look like 2026-10-12");
const isoDateTime = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "Times look like 2026-10-12T14:30");

export const itemFieldsSchema = z.object({
  date: isoDate,
  store: name,
  itemNo: z.string().trim().max(40),
  name,
  category: z.string().trim().max(40).nullable(),
  priceCents: cents,
  couponCents: cents.max(0, "Coupons are zero or negative"),
  crvCents: cents.min(0),
  taxBp: bp.nullable(),
  tipBp: bp.nullable().optional(),
  paidWith: z.string().trim().max(60).nullable(),
  paidBy: id.nullable(),
  customSplit: z.string().trim().max(200),
  note: z.string().max(500),
  // Optional with no default: a default would sneak into every partial update in Zod 4.
  receipt: z.string().trim().max(40).optional(),
  /** The receipt it's on, by id. Optional: decide() fills it in when the receipt already has one. */
  receiptId: z.string().trim().min(1).max(80).optional(),
});

export const catalogNoteSchema = z.object({
  keywords: z.array(z.string().trim().min(1).max(40)).max(20),
  icon: z.string().max(16).optional(),
  taxable: z.boolean().nullable().optional(),
  note: z.string().max(300).optional(),
  priceNowCents: z.number().int().min(0).max(10_000_000).optional(),
  priceSeenOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  source: z.enum(["you", "ai"]).optional(),
});

export const commandSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("group.create"), name }),
  z.object({
    type: z.literal("settings.update"),
    patch: z
      .object({
        ownerPersonId: id.nullable(),
        overdueWarnDays: z.number().int().min(1).max(365),
        overdueLateDays: z.number().int().min(1).max(365),
        categories: z.array(z.string().trim().min(1).max(40)).max(50),
        cards: z.array(z.string().trim().min(1).max(60)).max(50),
        memberAccess: z
          .object({ people: z.boolean(), receipts: z.boolean(), insights: z.boolean() })
          .partial(),
        treasurerAccess: z
          .object({
            people: z.boolean(),
            receipts: z.boolean(),
            insights: z.boolean(),
            history: z.boolean(),
            settings: z.boolean(),
          })
          .partial(),
        joining: z.enum(["open", "ask", "closed"]),
        receiptLocks: z
          .record(z.string().min(1).max(200), z.boolean())
          .refine((r) => Object.keys(r).length <= 5000, "Too many receipts"),
        receiptsOpenByDefault: z.boolean(),
        personAccess: z
          .record(
            id,
            z.object({
              pages: z
                .object({
                  people: z.boolean(),
                  receipts: z.boolean(),
                  insights: z.boolean(),
                  history: z.boolean(),
                  settings: z.boolean(),
                })
                .partial()
                .optional(),
              hiddenCategories: z.array(z.string().trim().min(1).max(40)).max(50).optional(),
            }),
          )
          .refine((r) => Object.keys(r).length <= 500, "Too many people"),
      })
      .partial(),
  }),
  z.object({
    type: z.literal("store.set"),
    name,
    taxBp: bp,
    tipBp: bp.optional(),
    mcc: z
      .string()
      .regex(/^\d{4}$/, "Merchant codes are 4 digits")
      .optional(),
    category: z.string().trim().min(1).max(40),
  }),
  z.object({ type: z.literal("store.remove"), name }),
  z.object({ type: z.literal("person.add"), personId: id, name, color: z.string().min(1).max(20) }),
  z.object({
    type: z.literal("person.update"),
    personId: id,
    patch: z.object({ name, color: z.string().min(1).max(20), archived: z.boolean() }).partial(),
  }),
  z.object({
    type: z.literal("item.add"),
    itemId: id,
    fields: itemFieldsSchema,
    ticked: z.array(id).max(50).default([]),
  }),
  z.object({ type: z.literal("item.update"), itemId: id, patch: itemFieldsSchema.partial() }),
  z.object({ type: z.literal("item.remove"), itemId: id }),
  z.object({ type: z.literal("share.set"), itemId: id, personId: id, ticked: z.boolean() }),
  z.object({
    type: z.literal("payment.add"),
    paymentId: id,
    at: isoDateTime,
    personId: id,
    cents: cents.refine((c) => c !== 0, "A payment can't be $0"),
    note: z.string().max(200).default(""),
  }),
  z.object({
    type: z.literal("payment.update"),
    paymentId: id,
    patch: z
      .object({
        at: isoDateTime,
        personId: id,
        cents: cents.refine((c) => c !== 0, "A payment can't be $0"),
        note: z.string().max(200),
      })
      .partial(),
  }),
  z.object({ type: z.literal("payment.remove"), paymentId: id }),
  z.object({
    type: z.literal("receipt.set"),
    date: isoDate,
    store: name,
    receipt: z.string().trim().max(40).optional(),
    /** The receipt by id (receipts from before ids are found by date, store and label). */
    receiptId: z.string().trim().min(1).max(80).optional(),
    cents: cents.min(0).nullable(),
  }),
  z.object({ type: z.literal("undo"), eventId: id }),
  z.object({
    type: z.literal("catalog.note"),
    key: z.string().min(2).max(120),
    note: catalogNoteSchema.nullable(),
  }),
  z.object({
    type: z.literal("import.mark"),
    source: z.string().max(200),
    capturedAt: isoDate,
  }),
]);

export type Command = z.infer<typeof commandSchema>;
export type CommandType = Command["type"];

/** A command plus the key that makes retries safe (the same key is never applied twice). */
export interface CommandEnvelope {
  cmdId: string;
  cmd: Command;
}

export const envelopeSchema = z.object({ cmdId: id, cmd: commandSchema });

/** Validates unknown input (from JSON) into a Command, with a readable message on failure. */
export function parseCommand(
  input: unknown,
): { ok: true; cmd: Command } | { ok: false; message: string } {
  const r = commandSchema.safeParse(input);
  if (r.success) return { ok: true, cmd: r.data };
  const first = r.error.issues[0];
  const where = first && first.path.length > 0 ? `${first.path.join(".")}: ` : "";
  return { ok: false, message: `${where}${first?.message ?? "Invalid command"}` };
}
